import { useEffect, useRef, useState } from "react";
import { CheckCircle2, Circle, ChevronDown, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import MetricListItem from "./MetricListItem";
import { formatCount } from "./metrics-drawer.utils";

const EXPAND_SKELETON_MS = 180;

const buildCountLabel = (visibleCount, totalCount) => {
  if (visibleCount === totalCount) return `(${formatCount(totalCount)})`;
  return `(${formatCount(visibleCount)} of ${formatCount(totalCount)})`;
};

const STATUS_CONFIG = {
  completed: {
    Icon: CheckCircle2,
    accent: "text-emerald-700 dark:text-emerald-300",
    icon: "border-emerald-500/20 bg-emerald-500/10",
  },
  missing: {
    Icon: Circle,
    accent: "text-amber-700 dark:text-amber-300",
    icon: "border-amber-500/20 bg-amber-500/10",
  },
};

const LoadingMetricItemSkeleton = ({ accent, icon }) => (
  <div className="rounded-lg border border-border/70 bg-card px-3 py-3 shadow-sm">
    <div className="flex items-start justify-between gap-3">
      <div className="flex min-w-0 items-start gap-3">
        <span
          className={cn(
            "mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border",
            icon,
          )}
        >
          <div
            className={cn(
              "h-4 w-4 animate-pulse rounded-full bg-current/30",
              accent,
            )}
          />
        </span>

        <div className="min-w-0 flex-1 space-y-2">
          <div className="h-4 w-56 max-w-full animate-pulse rounded bg-muted" />
          <div className="flex flex-wrap items-center gap-2">
            <div className="h-6 w-16 animate-pulse rounded-full bg-muted" />
            <div className="h-6 w-28 animate-pulse rounded-full bg-muted" />
            <div className="h-6 w-24 animate-pulse rounded-full bg-muted" />
          </div>
        </div>
      </div>

      <div className="h-9 w-9 shrink-0 animate-pulse rounded-md border border-border/70 bg-muted" />
    </div>
  </div>
);

const MetricsList = ({
  title,
  moduleName,
  metrics,
  totalCount,
  status,
  emptyMessage,
  loading = false,
  variant = "data",
  frameworkLabelByCode,
}) => {
  const [open, setOpen] = useState(false);
  const [isExpanding, setIsExpanding] = useState(false);
  const expandTimerRef = useRef(null);
  const isExpanded = open;
  const showSkeletonRows = isExpanded && (loading || isExpanding);

  const { Icon, accent, icon } = STATUS_CONFIG[status];

  useEffect(
    () => () => {
      if (expandTimerRef.current) {
        clearTimeout(expandTimerRef.current);
      }
    },
    [],
  );

  const handleToggle = () => {
    if (loading) return;

    if (isExpanded) {
      if (expandTimerRef.current) {
        clearTimeout(expandTimerRef.current);
        expandTimerRef.current = null;
      }
      setIsExpanding(false);
      setOpen(false);
      return;
    }

    setOpen(true);
    setIsExpanding(true);

    if (expandTimerRef.current) {
      clearTimeout(expandTimerRef.current);
    }

    expandTimerRef.current = setTimeout(() => {
      setIsExpanding(false);
      expandTimerRef.current = null;
    }, EXPAND_SKELETON_MS);
  };

  return (
    <div className="space-y-2.5">
      <button
        type="button"
        onClick={handleToggle}
        disabled={loading}
        className={cn(
          "flex w-full items-center justify-between px-0 py-1.5 text-left transition-opacity",
          loading ? "cursor-not-allowed opacity-60" : "hover:opacity-80",
        )}
      >
        <div className="flex items-center gap-3">
          <span
            className={cn(
              "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border",
              icon,
            )}
          >
            <Icon className={`h-4 w-4 ${accent}`} />
          </span>

          <div className="min-w-0">
            {loading ? (
              <div className="flex items-center gap-2 pt-0.5">
                <p
                  className={`text-[11px] font-semibold uppercase tracking-wide ${accent}`}
                >
                  {title}
                </p>
                <div className="h-3 w-10 rounded-sm bg-muted/60" />
              </div>
            ) : (
              <>
                <p
                  className={`text-[11px] font-semibold uppercase tracking-wide ${accent}`}
                >
                  {title} {buildCountLabel(metrics.length, totalCount)}
                </p>
              </>
            )}
          </div>
        </div>

        {isExpanded ? (
          <ChevronDown className={`h-4 w-4 ${accent}`} />
        ) : (
          <ChevronRight className={`h-4 w-4 ${accent}`} />
        )}
      </button>

      {showSkeletonRows ? (
        <div className="space-y-2.5">
          {Array.from({ length: 3 }).map((_, index) => (
            <LoadingMetricItemSkeleton
              key={`${status}-loading-${index}`}
              accent={accent}
              icon={icon}
            />
          ))}
        </div>
      ) : isExpanded ? (
        <div className="space-y-2.5">
          {metrics.length > 0 ? (
            <div className="space-y-2.5">
              {metrics.map((metric) => (
                <MetricListItem
                  key={metric.metricId}
                  metric={metric}
                  status={status}
                  variant={variant}
                  moduleName={moduleName}
                  frameworkLabelByCode={frameworkLabelByCode}
                />
              ))}
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-border/70 bg-muted px-4 py-8 text-center text-xs text-muted-foreground">
              {emptyMessage}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default MetricsList;
